import { TOKEN_STORAGE_KEYS } from '@onboarding-dashboard/shared';
import type { ApiResponse } from '@onboarding-dashboard/shared';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

class ApiClient {
  private baseUrl: string;
  private isRefreshing = false;
  private refreshPromise: Promise<boolean> | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getAccessToken(): string | null {
    return localStorage.getItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN);
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem(TOKEN_STORAGE_KEYS.REFRESH_TOKEN);
  }

  private setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    localStorage.setItem(TOKEN_STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  }

  clearTokens(): void {
    localStorage.removeItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(TOKEN_STORAGE_KEYS.REFRESH_TOKEN);
  }

  private buildHeaders(customHeaders?: Record<string, string>): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...customHeaders,
    };
    const token = this.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  private async refreshAccessToken(): Promise<boolean> {
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }
    this.isRefreshing = true;
    this.refreshPromise = this.doRefresh();
    const result = await this.refreshPromise;
    this.isRefreshing = false;
    this.refreshPromise = null;
    return result;
  }

  private async doRefresh(): Promise<boolean> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return false;

    const MAX_RETRIES = 3;
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const response = await fetch(`${this.baseUrl}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        // 4xx = don't retry (invalid/expired token)
        if (response.status >= 400 && response.status < 500) return false;
        // 5xx or other = retry after backoff
        if (!response.ok) {
          if (attempt < MAX_RETRIES - 1) {
            await new Promise((r) => setTimeout(r, 1000 * 2 ** attempt));
            continue;
          }
          return false;
        }
        const data: ApiResponse<{
          accessToken: string;
          refreshToken: string;
        }> = await response.json();
        if (data.success && data.data) {
          this.setTokens(data.data.accessToken, data.data.refreshToken);
          return true;
        }
        return false;
      } catch {
        // Network error (server down, DNS failure, etc.) — retry with backoff
        if (attempt < MAX_RETRIES - 1) {
          await new Promise((r) => setTimeout(r, 1000 * 2 ** attempt));
          continue;
        }
        return false;
      }
    }
    return false;
  }

  async request<T>(
    method: string,
    path: string,
    body?: unknown,
    customHeaders?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${path}`;
    const options: RequestInit = {
      method,
      headers: this.buildHeaders(customHeaders),
    };
    if (body) {
      options.body = JSON.stringify(body);
    }

    let response = await fetch(url, options);

    // Auto-refresh on 401
    if (response.status === 401 && this.getRefreshToken()) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        options.headers = this.buildHeaders(customHeaders);
        response = await fetch(url, options);
      }
    }

    const data: ApiResponse<T> = await response.json();

    // If the account was deactivated, clear tokens and force re-login
    if (data.error?.code === 'ACCOUNT_INACTIVE') {
      this.clearTokens();
      window.location.href = '/login';
      return data;
    }

    if (!response.ok && !data.error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: `Request failed with status ${response.status}`,
        },
      };
    }

    return data;
  }

  get<T>(path: string): Promise<ApiResponse<T>> {
    return this.request<T>('GET', path);
  }

  post<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>('POST', path, body);
  }

  patch<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', path, body);
  }

  put<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', path, body);
  }

  delete<T>(path: string): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', path);
  }

  async upload<T>(
    path: string,
    formData: FormData,
    method: 'POST' | 'PATCH' = 'POST'
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${path}`;
    const headers: Record<string, string> = {};
    const token = this.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // Do NOT set Content-Type — the browser sets it with the boundary for multipart

    let response = await fetch(url, {
      method,
      headers,
      body: formData,
    });

    if (response.status === 401 && this.getRefreshToken()) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        const retryHeaders: Record<string, string> = {};
        const newToken = this.getAccessToken();
        if (newToken) retryHeaders['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(url, {
          method,
          headers: retryHeaders,
          body: formData,
        });
      }
    }

    const data: ApiResponse<T> = await response.json();

    if (data.error?.code === 'ACCOUNT_INACTIVE') {
      this.clearTokens();
      window.location.href = '/login';
      return data;
    }

    if (!response.ok && !data.error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: `Request failed with status ${response.status}`,
        },
      };
    }

    return data;
  }
}

export const apiClient = new ApiClient(API_BASE);
